import { getEmploymentFieldVisibility } from "web-apps/company/components/staff/components/shared/staffEmploymentFieldVisibility.logic";
import {
  getEmployeeNumberValidationError,
  getEmploymentDaysValidationError,
  getNameValidationError,
  getOptionalEmailValidationError,
  getOptionalPersonalIdentityNumberValidationError,
  getOptionalTextValidationError,
  getPhoneValidationError,
  getWorkPercentageValidationError,
  hasValidEmployeeNumber,
  hasValidName,
  hasValidOptionalEmail,
  hasValidOptionalEmploymentDays,
  hasValidOptionalPersonalIdentityNumber,
  hasValidOptionalText,
  hasValidPhoneNumber,
  hasValidRequiredText,
  hasValidWorkPercentage,
  type StaffInputValidationError,
} from "web-apps/company/components/staff/components/shared/staffInputValidation.logic";
import type {
  EditingTab,
  EmploymentDetailsForm,
  EmploymentSaveMode,
  PersonalDetailsForm,
} from "../profilePanel.contract";

interface CanSaveEmploymentEditParams {
  readonly editedEmploymentDetails: EmploymentDetailsForm;
  readonly editingTab: EditingTab;
  readonly employmentSaveMode: EmploymentSaveMode;
  readonly isEndDateRequiredOnSave: boolean;
}

export interface PersonalFieldValidationErrors {
  readonly email: StaffInputValidationError | null;
  readonly firstName: StaffInputValidationError | null;
  readonly lastName: StaffInputValidationError | null;
  readonly personalIdentityNumber: StaffInputValidationError | null;
  readonly phoneNumber: StaffInputValidationError | null;
}

export interface EmploymentFieldValidationErrors {
  readonly agreement: StaffInputValidationError | null;
  readonly employeeNumber: StaffInputValidationError | null;
  readonly noticeDays: StaffInputValidationError | null;
  readonly role: StaffInputValidationError | null;
  readonly vacationDays: StaffInputValidationError | null;
  readonly workplace: StaffInputValidationError | null;
  readonly workPercentage: StaffInputValidationError | null;
}

function hasNonEmptyValue(value: string): boolean {
  return value.trim().length > 0;
}

function hasValidNumericValue(value: string): boolean {
  const normalizedValue = value.trim();
  if (normalizedValue.length === 0) return false;
  return Number.isFinite(Number(normalizedValue));
}

export function canSavePersonalEdit(form: PersonalDetailsForm): boolean {
  return (
    hasValidPhoneNumber({ value: form.phoneNumber }) &&
    hasValidName({ value: form.firstName }) &&
    hasValidName({ value: form.lastName }) &&
    hasValidOptionalEmail({ value: form.email }) &&
    hasValidOptionalPersonalIdentityNumber({
      value: form.personalIdentityNumber,
    })
  );
}

export function getPersonalFieldValidationErrors(
  form: PersonalDetailsForm,
): PersonalFieldValidationErrors {
  return {
    email: getOptionalEmailValidationError({ value: form.email }),
    firstName: getNameValidationError({
      field: "firstName",
      value: form.firstName,
    }),
    lastName: getNameValidationError({
      field: "lastName",
      value: form.lastName,
    }),
    personalIdentityNumber: getOptionalPersonalIdentityNumberValidationError({
      value: form.personalIdentityNumber,
    }),
    phoneNumber: getPhoneValidationError({ value: form.phoneNumber }),
  };
}

export function getEmploymentFieldValidationErrors(
  form: EmploymentDetailsForm,
): EmploymentFieldValidationErrors {
  return {
    agreement: getOptionalTextValidationError({
      translationKey: "Staff.AddStaffPanelAgreementTooLong",
      value: form.agreement,
    }),
    employeeNumber: getEmployeeNumberValidationError({
      value: form.employeeNumber,
    }),
    noticeDays: getEmploymentDaysValidationError({ value: form.noticeDays }),
    role: getOptionalTextValidationError({
      translationKey: "Staff.AddStaffPanelRoleTooLong",
      value: form.role,
    }),
    vacationDays: getEmploymentDaysValidationError({
      value: form.vacationDays,
    }),
    workplace: getOptionalTextValidationError({
      translationKey: "Staff.AddStaffPanelWorkplaceTooLong",
      value: form.workplace,
    }),
    workPercentage: getWorkPercentageValidationError({
      value: form.workPercentage,
    }),
  };
}

function hasValidCoreEmploymentFields({
  form,
  isEndDateRequired,
}: {
  readonly form: EmploymentDetailsForm;
  readonly isEndDateRequired: boolean;
}): boolean {
  if (!hasNonEmptyValue(form.employmentType)) return false;
  if (
    !hasValidEmployeeNumber({ isRequired: true, value: form.employeeNumber })
  ) {
    return false;
  }
  if (!hasValidRequiredText({ value: form.role })) return false;
  if (form.startDate == null) return false;
  if (isEndDateRequired && form.endDate == null) return false;
  return form.endDate == null || form.endDate.isAfter(form.startDate);
}

function hasValidContractTerms(form: EmploymentDetailsForm): boolean {
  return (
    hasValidOptionalText({ value: form.agreement }) &&
    hasValidOptionalEmploymentDays({ value: form.noticeDays }) &&
    hasValidOptionalEmploymentDays({ value: form.vacationDays }) &&
    hasValidOptionalText({ value: form.workplace })
  );
}

function hasValidVisibleEmploymentFields(form: EmploymentDetailsForm): boolean {
  const visibility = getEmploymentFieldVisibility(form.employmentType);
  if (
    visibility.showEmployeeCategory &&
    !hasNonEmptyValue(form.employeeCategory)
  ) {
    return false;
  }
  if (visibility.showContractTerms && !hasValidContractTerms(form))
    return false;
  if (
    visibility.showSalaryType &&
    form.salaryType === "Monthly" &&
    !hasValidWorkPercentage({ value: form.workPercentage })
  ) {
    return false;
  }
  return true;
}

function hasValidCreateOnlyFields(form: EmploymentDetailsForm): boolean {
  const visibility = getEmploymentFieldVisibility(form.employmentType);
  if (visibility.showSalaryType && !hasNonEmptyValue(form.salaryType)) {
    return false;
  }
  if (visibility.showSalary && !hasValidNumericValue(form.salary)) return false;
  return !visibility.showPaymentType || hasNonEmptyValue(form.paymentType);
}

export function canSaveEmploymentEdit({
  editedEmploymentDetails,
  editingTab,
  employmentSaveMode,
  isEndDateRequiredOnSave,
}: CanSaveEmploymentEditParams): boolean {
  if (editingTab !== "employmentSalary") return true;
  if (
    !hasValidCoreEmploymentFields({
      form: editedEmploymentDetails,
      isEndDateRequired: isEndDateRequiredOnSave,
    })
  )
    return false;
  if (!hasValidVisibleEmploymentFields(editedEmploymentDetails)) return false;
  return (
    employmentSaveMode !== "create" ||
    hasValidCreateOnlyFields(editedEmploymentDetails)
  );
}
